/**
 * ScrollTrigger Line Reveal Animation – Text & Parallax Image Blend Engine
 *
 * Implements dramatic 3D Text Drop line reveals (rotateX: -120deg -> 0deg)
 * with perspective, backface culling, and ScrollTrigger scrub, paired with
 * smooth parallax image depth for non-slider images.
 *
 * Automatically splits multi-line text and headings by line breaks (<br>)
 * and natural visual wrapping lines so that each line animates independently.
 *
 * Excludes:
 *  - Homepage Intro Loader (strictly untouched)
 *  - Homepage Hero 3D Cylinder, headline group, and slider carousels
 *
 * Architecture: Nirajan <https://nbk.com.np>
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export class ScrollRevealEngine {
  constructor() {
    this.mm = gsap.matchMedia();
    this.resizeTimeout = null;
    this.lastWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
    this.onResize = this.handleResize.bind(this);

    this.mm.add('(prefers-reduced-motion: no-preference)', () => {
      this.initTextDrop();
      this.initButtons();
      this.initImageDrop();
      this.initParallaxImages();
      this.initMethodCards();
      this.initAboutTextReveal();
      window.addEventListener('resize', this.onResize);
    });

    this.mm.add('(prefers-reduced-motion: reduce)', () => {
      gsap.set(
        '.text-drop__line, .text-drop__eyebrow, .text-drop__btn, .nbk-text-reveal, .method-step-card, #about-profile-stage',
        {
          rotateX: 0,
          opacity: 1,
          y: 0,
          scale: 1,
        },
      );
      gsap.set('.chroma-heading', { backgroundPosition: '0% 0' });
      gsap.set('.has-prlx', { y: 0 });
      gsap.set('.text-drop__img-box', { opacity: 1 });
      gsap.set('.text-drop__img-reveal, .curved-slider-container', {
        rotateX: 0,
        y: 0,
        opacity: 1,
      });
      gsap.set('.curved-screen-item img', { scale: 1 });
      gsap.set('.about-reveal-word', {
        color: '#ffffff',
        opacity: 1,
        textShadow: 'none',
        y: 0,
      });
    });
  }

  /**
   * Automatically split a text element by <br> line breaks and visual wrapped lines
   * into independent .text-drop__line spans.
   *
   * @param {HTMLElement} el The element to split.
   */
  splitElementIntoLines(el) {
    if (!el || el.dataset.splitDone === 'true') return;

    // Strictly skip Hero section, Intro loader, Arc Carousel, Spiral Showcase, and form elements
    if (
      el.closest('#nbk-intro-loader') ||
      el.closest('#hero-headline-group') ||
      el.closest('#points-pin-wrapper') ||
      el.closest('#spiral-showcase-wrapper') ||
      el.closest('.spiral-card') ||
      el.closest('form') ||
      el.id === 'hero-headline-group' ||
      el.id === 'hero-headline'
    ) {
      return;
    }

    // Skip non-text visual components, sliders, and form buttons
    if (
      el.tagName === 'P' ||
      el.closest('p') ||
      el.classList.contains('about-reveal-p') ||
      el.closest('.about-reveal-p') ||
      el.classList.contains('about-reveal-word') ||
      el.closest('.about-reveal-word') ||
      el.classList.contains('text-drop__eyebrow') ||
      el.closest('.text-drop__eyebrow') ||
      el.hasAttribute('data-no-split') ||
      el.closest('[data-no-split="true"]') ||
      el.classList.contains('chip-fan-deck') ||
      el.classList.contains('text-drop__btn') ||
      el.classList.contains('text-drop__img-box') ||
      el.classList.contains('curved-slider-wrapper') ||
      el.classList.contains('curved-slider-container') ||
      el.classList.contains('curved-screen-item') ||
      el.classList.contains('spiral-card') ||
      el.classList.contains('spiral-cylinder-stage') ||
      el.matches('svg, img, input, textarea, button, select') ||
      el.querySelector(
        'svg, img, input, textarea, button, select, .chip-fan-deck, .text-drop__btn, .curved-slider-container, .spiral-cylinder-stage',
      )
    ) {
      el.dataset.splitDone = 'true';
      return;
    }

    // If already contains .text-drop__line child elements (e.g. manually structured), mark done
    if (el.querySelector('.text-drop__line')) {
      el.dataset.splitDone = 'true';
      return;
    }

    // If it's already an atomic span.text-drop__line with NO <br>, it's already a single line
    const hasBr = !!el.querySelector('br');
    if (el.tagName === 'SPAN' && el.classList.contains('text-drop__line') && !hasBr) {
      el.dataset.splitDone = 'true';
      return;
    }

    const link = el.querySelector('a');
    const target = link || el;

    if (!target.dataset.originalHtml) {
      target.dataset.originalHtml = target.innerHTML;
    }

    const originalHtml = target.dataset.originalHtml;
    if (!originalHtml || !target.textContent.trim()) return;

    // Split by <br> or <br/> tags
    const brRegex = /<br\s*[/]?>/gi;
    const segments = originalHtml.split(brRegex);

    const textContent = target.textContent.trim();
    if (segments.length === 1 && !textContent.includes(' ')) {
      target.innerHTML = `<span class="text-drop__line block">${textContent}</span>`;
      el.classList.remove('text-drop__line');
      el.classList.add('text-drop-stage');
      el.style.transformStyle = 'preserve-3d';
      target.dataset.splitDone = 'true';
      return;
    }

    // Measure visual lines per segment
    const allFinalLines = [];
    target.innerHTML = '';

    segments.forEach((segHtml) => {
      const segContainer = document.createElement('span');
      segContainer.style.display = 'block';
      segContainer.innerHTML = segHtml.trim();

      const text = segContainer.textContent.trim();
      if (!text) return;

      const words = text.split(/\s+/).filter(Boolean);
      if (words.length === 0) return;

      if (words.length === 1) {
        allFinalLines.push(words[0]);
        return;
      }

      // Populate with inline word spans to measure offsetTop
      segContainer.innerHTML = '';
      const wordSpans = words.map((w) => {
        const s = document.createElement('span');
        s.style.display = 'inline-block';
        s.textContent = w;
        segContainer.appendChild(s);
        segContainer.appendChild(document.createTextNode(' '));
        return s;
      });

      target.appendChild(segContainer);

      const segLines = [];
      let currentLineWords = [];
      let lastTop = null;

      wordSpans.forEach((span) => {
        const top = Math.round(span.offsetTop);
        if (lastTop === null) {
          lastTop = top;
        }

        if (Math.abs(top - lastTop) > 4) {
          if (currentLineWords.length) {
            segLines.push(currentLineWords.join(' '));
            currentLineWords = [];
          }
          lastTop = top;
        }

        currentLineWords.push(span.textContent);
      });

      if (currentLineWords.length) {
        segLines.push(currentLineWords.join(' '));
      }

      segLines.forEach((l) => allFinalLines.push(l));

      target.removeChild(segContainer);
    });

    if (allFinalLines.length === 0) {
      target.innerHTML = originalHtml;
      return;
    }

    // Construct final DOM with independent .text-drop__line block spans
    target.innerHTML = '';
    allFinalLines.forEach((lineText) => {
      const lineSpan = document.createElement('span');
      lineSpan.className = 'text-drop__line block';
      lineSpan.style.display = 'block';
      lineSpan.style.transformOrigin = '50% 0';
      lineSpan.textContent = lineText;
      target.appendChild(lineSpan);
    });

    // Remove text-drop__line from parent container so it doesn't double-animate
    el.classList.remove('text-drop__line');
    el.classList.add('text-drop-stage');
    el.style.transformStyle = 'preserve-3d';
    target.dataset.splitDone = 'true';
  }

  splitAllTextTargets() {
    const candidates = document.querySelectorAll(
      '.text-drop-stage h1, .text-drop-stage h2, .text-drop-stage h3, .text-drop-stage h4, .text-drop-stage h5, .text-drop-stage h6, .text-drop__text, .text-drop__split',
    );

    candidates.forEach((el) => {
      if (
        !el.closest('form') &&
        !el.closest('#points-pin-wrapper') &&
        !el.closest('#spiral-showcase-wrapper') &&
        !el.closest('#about-title') &&
        !el.matches('#about-title') &&
        !el.hasAttribute('data-no-split') &&
        !el.closest('[data-no-split="true"]')
      ) {
        this.splitElementIntoLines(el);
      }
    });
  }

  initTextDrop() {
    this.splitAllTextTargets();

    // Select text reveal targets while strictly excluding buttons, forms, arc carousel, and spiral showcase
    const rawLines = gsap.utils.toArray('.text-drop__eyebrow, .text-drop__line, .nbk-text-reveal');
    const lines = rawLines.filter(
      (item) =>
        !item.closest('form') &&
        !item.closest('#points-pin-wrapper') &&
        !item.closest('#spiral-showcase-wrapper') &&
        !item.closest('.text-drop__btn') &&
        !item.matches(
          'button, input[type="submit"], textarea, select, .text-drop__btn, .nbk-pill-btn',
        ),
    );

    lines.forEach((line, i) => {
      const parent = line.parentElement;
      if (parent) {
        parent.style.perspective = parent.style.perspective || '2000px';
        parent.style.transformStyle = 'preserve-3d';
      }

      // Check if this line is part of any heading tag (h1, h2, h3, h4, h5, h6)
      const isHeading =
        !!line.closest('h1, h2, h3, h4, h5, h6') || line.matches('h1, h2, h3, h4, h5, h6');
      if (isHeading) {
        line.classList.add('chroma-heading');
      }

      // Calculate sibling cascade stagger
      const siblings = parent ? Array.from(parent.querySelectorAll('.text-drop__line')) : [];
      const siblingIndex = siblings.indexOf(line);
      const staggerOffset = siblingIndex > 0 ? Math.min(siblingIndex * 2.5, 20) : 0;
      const startPercent = Math.max(70, Math.round(92 - staggerOffset));
      const endPercent = Math.max(40, Math.round(60 - staggerOffset));

      const fromProps = {
        rotateX: -120,
        opacity: 0,
        transformOrigin: '50% 0',
      };

      const toProps = {
        rotateX: 0,
        opacity: 1,
        ease: 'power2.out',
        scrollTrigger: {
          id: `text-drop-line-${i}`,
          trigger: line,
          start: `top ${startPercent}%`,
          end: `bottom ${endPercent}%`,
          scrub: 0.8,
        },
      };

      if (isHeading) {
        fromProps.backgroundPosition = '100% 0';
        toProps.backgroundPosition = '0% 0';
      }

      gsap.fromTo(line, fromProps, toProps);
    });

    ScrollTrigger.refresh();
  }

  /**
   * Action & CTA Buttons Reveal:
   * Decoupled from 3D rotateX and scrub so buttons are immediately responsive,
   * hoverable, and clickable at any position in the viewport as soon as they enter.
   */
  initButtons() {
    const rawButtons = gsap.utils.toArray('.text-drop__btn, .nbk-pill-btn');
    const validButtons = rawButtons.filter(
      (btn) =>
        !btn.closest('form') &&
        !btn.closest('#points-pin-wrapper') &&
        !btn.closest('#spiral-showcase-wrapper') &&
        !btn.closest('#nbk-intro-loader') &&
        !btn.closest('#hero') &&
        !btn.closest('#intro'),
    );

    validButtons.forEach((btn, i) => {
      gsap.fromTo(
        btn,
        {
          opacity: 0,
          y: 24,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            id: `text-drop-btn-${i}`,
            trigger: btn,
            start: 'top 92%',
            once: true,
          },
          onComplete: () => {
            gsap.set(btn, { clearProps: 'transform,opacity' });
          },
        },
      );
    });
  }

  handleResize() {
    if (typeof window === 'undefined') return;
    if (window.innerWidth === this.lastWidth) return;
    this.lastWidth = window.innerWidth;

    window.clearTimeout(this.resizeTimeout);
    this.resizeTimeout = window.setTimeout(() => {
      this.reSplitAndRefresh();
    }, 250);
  }

  reSplitAndRefresh() {
    const splitElements = document.querySelectorAll('[data-split-done="true"]');
    splitElements.forEach((el) => {
      if (
        el.dataset.originalHtml &&
        el.tagName !== 'P' &&
        !el.classList.contains('text-drop__eyebrow') &&
        !el.closest('.text-drop__eyebrow') &&
        !el.closest('#about-title') &&
        !el.matches('#about-title') &&
        !el.hasAttribute('data-no-split') &&
        !el.closest('[data-no-split="true"]')
      ) {
        el.innerHTML = el.dataset.originalHtml;
        delete el.dataset.splitDone;
      }
    });

    ScrollTrigger.getAll().forEach((trigger) => {
      if (
        trigger.vars.id &&
        (trigger.vars.id.startsWith('text-drop-line-') ||
          trigger.vars.id.startsWith('text-drop-btn-') ||
          trigger.vars.id.startsWith('text-drop-img-') ||
          trigger.vars.id.startsWith('about-words-') ||
          trigger.vars.id.startsWith('about-profile-') ||
          trigger.vars.id.startsWith('about-narrative') ||
          trigger.vars.id.startsWith('about-reveal-line-'))
      ) {
        trigger.kill();
      }
    });

    this.initTextDrop();
    this.initButtons();
    this.initImageDrop();
    this.initAboutTextReveal();
    ScrollTrigger.refresh();
  }

  /**
   * 3D Perspective Scroll Reveal for Showcase Sliders and Imagery
   * Matches the signature text-drop tumble effect (rotateX hinged at top, scrubbed with scroll).
   */
  initImageDrop() {
    const imgContainers = gsap.utils.toArray('.text-drop__img-reveal, .curved-slider-container');
    const validContainers = imgContainers.filter(
      (el) =>
        !el.closest('#nbk-intro-loader') &&
        !el.closest('#hero') &&
        !el.closest('#intro') &&
        !el.closest('#points-pin-wrapper') &&
        !el.closest('#spiral-showcase-wrapper'),
    );

    validContainers.forEach((container, i) => {
      const parent = container.parentElement;
      if (parent) {
        parent.style.perspective = parent.style.perspective || '2000px';
        parent.style.transformStyle = 'preserve-3d';
      }

      // 3D Perspective tumble & drop reveal matching text-drop
      gsap.fromTo(
        container,
        {
          rotateX: -110,
          y: 60,
          opacity: 0,
          transformOrigin: '50% 0',
        },
        {
          rotateX: 0,
          y: 0,
          opacity: 1,
          ease: 'power2.out',
          scrollTrigger: {
            id: `text-drop-img-${i}`,
            trigger: container,
            start: 'top 92%',
            end: 'top 52%',
            scrub: 0.8,
          },
        },
      );

      // Smooth inner image zoom settle as slider tumbles into place
      const innerImages = container.querySelectorAll('.curved-screen-item img');
      if (innerImages.length) {
        gsap.fromTo(
          innerImages,
          {
            scale: 1.15,
          },
          {
            scale: 1,
            ease: 'power2.out',
            scrollTrigger: {
              id: `text-drop-img-zoom-${i}`,
              trigger: container,
              start: 'top 92%',
              end: 'top 52%',
              scrub: 0.8,
            },
          },
        );
      }
    });
  }

  initParallaxImages() {
    const prlxElements = gsap.utils.toArray('.has-prlx');
    prlxElements.forEach((el) => {
      const speed = parseFloat(el.getAttribute('data-speed')) || 0.3;

      gsap.to(el, {
        y: () => -(1 - speed) * 120,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.5,
        },
      });
    });

    const images = gsap.utils.toArray('.text-drop__img-box');
    images.forEach((imgBox) => {
      const targetOpacity = parseFloat(imgBox.getAttribute('data-opacity')) || 1;

      gsap.fromTo(
        imgBox,
        { opacity: 0.2 },
        {
          opacity: targetOpacity,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: imgBox,
            start: 'top 90%',
            end: 'top 60%',
            scrub: 0.5,
          },
        },
      );
    });
  }

  /**
   * HOW I WORK: Scroll-Driven Moving Circuit Background & Alternating Card Reveals
   * Animates --strokeDashoffset on scroll, and reveals alternating left/right cards.
   */
  initMethodCards() {
    const section = document.getElementById('how-i-work');
    if (!section) return;

    // 1. Moving SVG Circuit Dashoffset on scroll
    ScrollTrigger.create({
      id: 'how-i-work-circuit',
      trigger: section,
      start: 'top bottom',
      end: 'bottom top',
      scrub: 0.35,
      onUpdate: (self) => {
        const offset = -2600 * self.progress;
        section.style.setProperty('--strokeDashoffset', offset);
      },
    });

    // 2. Alternating Left / Right Card Reveal Sequences
    const cardWrappers = gsap.utils.toArray('.method-card-wrapper');
    cardWrappers.forEach((wrapper, i) => {
      const card = wrapper.querySelector('.method-step-card') || wrapper;
      const isLeft = wrapper.classList.contains('method-card--left');

      // Chroma heading inside the card (sweeps from iridescent to bright white)
      const title = card.querySelector('.chroma-heading');
      if (title) {
        gsap.set(title, { backgroundPosition: '100% 0' });
      }

      gsap.fromTo(
        card,
        {
          opacity: 0,
          y: 40,
          x: isLeft ? -25 : 25,
          rotateX: -15,
          transformOrigin: isLeft ? '0% 50%' : '100% 50%',
        },
        {
          opacity: 1,
          y: 0,
          x: 0,
          rotateX: 0,
          duration: 0.85,
          ease: 'power3.out',
          scrollTrigger: {
            id: `how-i-work-card-${i}`,
            trigger: wrapper,
            start: 'top 85%',
            toggleActions: 'play none none none',
            onEnter: () => {
              if (title) {
                gsap.to(title, {
                  backgroundPosition: '0% 0',
                  duration: 0.95,
                  ease: 'power2.out',
                });
              }
            },
          },
        },
      );
    });
  }

  /**
   * Kinetic Word-by-Word Scroll-Driven Text Reveal for About Section.
   * Chained sequentially on a single master ScrollTrigger timeline on #about-narrative
   * so that Paragraph 1 finishes completely before Paragraph 2 begins, eliminating overlap.
   */
  initAboutTextReveal() {
    // 1. Centerpiece Profile Stage Entrance Transition
    const profileStage = document.getElementById('about-profile-stage');
    if (profileStage) {
      gsap.fromTo(
        profileStage,
        {
          opacity: 0,
          y: 32,
          scale: 0.96,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.85,
          ease: 'power3.out',
          scrollTrigger: {
            id: 'about-profile-reveal',
            trigger: profileStage,
            start: 'top 88%',
            once: true,
          },
        },
      );
    }

    // 2. Word-by-Word Reveal for Narrative Column
    const paragraphs = document.querySelectorAll('.about-reveal-p');
    if (!paragraphs.length) return;

    const paragraphWordGroups = [];

    paragraphs.forEach((p) => {
      // Find all pre-rendered or existing word spans
      let words = p.querySelectorAll('.about-reveal-word');

      // Auto-tokenize if words are not pre-rendered
      if (!words.length) {
        const rawText = p.textContent.trim();
        const tokens = rawText.split(/\s+/).filter(Boolean);
        p.innerHTML = tokens
          .map((word) => `<span class="about-reveal-word">${word}</span>`)
          .join(' ');
        words = p.querySelectorAll('.about-reveal-word');
      }

      if (words.length) {
        // Set initial high-contrast readable state
        gsap.set(words, {
          color: '#cbd5e1',
          opacity: 0.85,
          textShadow: 'none',
          y: 0,
        });
        paragraphWordGroups.push(words);
      }
    });

    if (!paragraphWordGroups.length) return;

    // Trigger on the narrative container to sequence all paragraphs smoothly
    const narrativeContainer =
      document.getElementById('about-narrative') || paragraphs[0].parentElement;

    const masterTl = gsap.timeline({
      scrollTrigger: {
        id: 'about-narrative-scrub',
        trigger: narrativeContainer,
        start: 'top 82%',
        end: 'bottom 55%',
        scrub: 0.35,
      },
    });

    // Sequence each paragraph strictly one after another
    paragraphWordGroups.forEach((words, pIndex) => {
      masterTl.to(
        words,
        {
          keyframes: [
            {
              color: '#38bdf8',
              opacity: 1,
              textShadow: 'none',
              y: 0,
              duration: 0.25,
            },
            {
              color: '#ffffff',
              opacity: 1,
              textShadow: 'none',
              y: 0,
              duration: 0.75,
            },
          ],
          stagger: 0.045,
          ease: 'none',
        },
        pIndex === 0 ? 0 : '+=0.08',
      );
    });
  }

  destroy() {
    window.removeEventListener('resize', this.onResize);
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    this.mm.revert();
  }
}

export const TextDropEngine = ScrollRevealEngine;
